/* eslint-disable no-underscore-dangle */
import humps from 'humps';
import cloneDeep from 'lodash/cloneDeep';
import {
  ActionTypes,
  BotSaysKeys,
  DEFAULT_LANGUAGE,
} from 'constants/MogamboConstants';

import { register } from '@src/dispatcher';
import CommonStore from '@src/stores/CommonStore';
import BotStore from '@src/store';
import createStore, { updateDevicePlatformMapDefault } from './StoreUtils.js';

/**
 * Bot Says Store
 * All the store values that are related to bot responses are stored here
 * @type {[type]}
 */
const BotSaysStore = createStore({
  /**
   * _botResponses: array of bot responses for a particular node
   * _entitySearchData: array of search results for entity search
   * _existingEntityNamesData: @TODO
   * @type {[type]}
   */
  _botResponses: {},
  _originalResponses: {},
  _entitySearchData: [],
  _existingEntityNamesData: undefined,
  _supportedLanguages: [],
  _selectedBotSaysLanguage: DEFAULT_LANGUAGE,
  _editedLanguages: [],
  _previousEntitiesExist: false,
  _isSuperman: false,
  _currentHybridLanguage: DEFAULT_LANGUAGE,
  _currentChannelID: 5,
  _botResponsesWithChannel: {},
  webSDKSaved: false,

  /**
   * Reset the store back to it's original values
   */
  reset() {
    this._botResponses = {};
    this._originalResponses = {};
    this._entitySearchData = [];
    this._existingEntityNamesData = undefined;
    this._selectedBotSaysLanguage = DEFAULT_LANGUAGE;
    this._editedLanguages = [];
    this._previousEntitiesExist = false;
    this._currentHybridLanguage = DEFAULT_LANGUAGE;
    this._currentChannelID = 5;
    this._botResponsesWithChannel = {};
    this.webSDKSaved = false;
  },

  /**
   * Set isSuperman
   */
  setIsSuperman(isSuperman) {
    this._isSuperman = isSuperman;
  },

  /**
   * Get isSuperman
   */
  getIsSuperman() {
    return this._isSuperman;
  },

  /**
   * Reset all edited responses
   */
  resetEdited() {
    if (this._botResponses) {
      this._previousEntitiesExist = false;
      this.setBotResponses(this._originalResponses, null, false);
      this._editedSynonyms = [];
    }
    this._currentChannelID = 5;
    this._editedLanguages = [];
  },

  /**
   * Set supported languages
   */
  setSupportedLanguages(languages, allLanguages) {
    const allSupportedLanguages = [];
    const hybridLanguages = Object.keys(
      CommonStore.getMetaTags()?.hybridLanguageSources
    );
    allLanguages.forEach((language) => {
      if (languages.includes(language.code)) {
        allSupportedLanguages.push({
          path: language.code,
          title: language.name,
        });
      }
    });

    if (hybridLanguages?.length > 0) {
      hybridLanguages.forEach((item) => {
        allSupportedLanguages.push({
          path: item,
        });
      });
    }
    this._supportedLanguages = allSupportedLanguages;
  },

  /**
   * Get all supported languages
   */
  getSupportedLanguages() {
    return this._supportedLanguages;
  },

  /**
   * Get current selected language
   */
  getCurrentBotSaysLanguage() {
    return this._selectedBotSaysLanguage;
  },

  /**
   * Get Edited languages
   */
  getEditedLanguages() {
    return this._editedLanguages;
  },

  /**
   * Reset Edited languages
   */
  resetEditedLanguages() {
    this._editedLanguages = [];
  },

  /**
   * Get WebSDKSaved
   */
  getWebSDKSaved() {
    return this.webSDKSaved;
  },

  /**
   * Set WebSDKSaved
   */
  setWebSDKSaved() {
    this.webSDKSaved = true;
  },

  /**
   * Set current selected language
   */
  setCurrentBotSaysLanguage(language, hybridLanguage) {
    if (!this._isSuperman) {
      this._editedLanguages = [];
      this._editedLanguages.push(language);
    }

    if (Object.keys(this._botResponsesWithChannel).length > 0) {
      this.removeAutoTranslatedChannelData();
    }

    /**
     * If hybridLanguage is not same as source language or language then
     * the currentHybridLanguage will be hybridLanguage|language
     * Otherwise, it will be hybridLanguage or language
     * */
    if (hybridLanguage && hybridLanguage !== language) {
      this._currentHybridLanguage = `${hybridLanguage}|${language}`;
    } else {
      this._currentHybridLanguage = hybridLanguage || language;
    }

    this._selectedBotSaysLanguage = language;
    this._currentChannelID = 5;
  },

  /**
   * Main getter to get all bot responses
   * @return {[type]} [description]
   */
  getBotResponses() {
    return this._botResponses;
  },

  /**
   * Returns flag whether previous entities exist or no
   * @return {[type]} [description]
   */
  getPreviousEntitiesFlag() {
    return this._previousEntitiesExist;
  },

  /**
   * Main setter to save all the bot responses to the store
   * We also initialize with some defaults
   * @param {[type]}   responses [description]
   * @param {Function} callback  [description]
   */
  setBotResponses(responses, callback, isUpdate) {
    const allSupportedLanguages = [...this._supportedLanguages];
    let previousEntities = [];
    const isSuperman = this._isSuperman;

    BotSaysStore.reset();

    if (!isUpdate) {
      this._originalResponses = JSON.parse(JSON.stringify(responses));
    }

    // Checks if any entity exists in next language
    const checkForEntities = isSuperman && allSupportedLanguages.length > 1;

    // Adds defaults to languages
    allSupportedLanguages.forEach((item) => {
      if (responses[item.path]) {
        this._botResponses[item.path] = responses[item.path];
        const botResponse = this._botResponses[item.path];

        if (!botResponse.botSays.variants) {
          botResponse.botSays.variants = [];
        }

        if (!botResponse.botSays.quickReplies) {
          botResponse.botSays.quickReplies = [];
        }

        if (!botResponse.botSays.devicePlatformMap) {
          botResponse.botSays.devicePlatformMap = {};
        }

        if (!botResponse.finalBotReply.variants) {
          botResponse.finalBotReply.variants = [];
        }

        if (!botResponse.finalBotReply.quickReplies) {
          botResponse.finalBotReply.quickReplies = [];
        }

        if (!botResponse.finalBotReply.devicePlatformMap) {
          botResponse.finalBotReply.devicePlatformMap = {};
        }

        if (!botResponse.delay.variants) {
          botResponse.delay.variants = [];
        }

        // Checks if entities exist in any of the language
        if (
          checkForEntities &&
          botResponse.entities.length > 0 &&
          previousEntities.length === 0
        ) {
          previousEntities = botResponse.entities;
          this._previousEntitiesExist = true;
        }

        // If entities exist in any of the language, then set those to all other languages
        if (
          previousEntities.length !== 0 &&
          botResponse.entities.length === 0
        ) {
          const allEntities = [];
          previousEntities.forEach((entity) => {
            const entityData = { ...entity };
            entityData.variants = [];
            entityData.quickReplies = [];
            allEntities.push(entityData);
          });
          botResponse.entities = allEntities;
          this._botResponses[item.path].updated = true;

          const languagesEdited = [...this._editedLanguages];
          if (!languagesEdited.includes(item.path)) {
            languagesEdited.push(item.path);
          }
          this._editedLanguages = languagesEdited;
        } else {
          this._previousEntitiesExist = false;
        }

        // If non-mandatory responses exist, then set them in all entities
        if (this._botResponses[item.path].nonMandatoryResponses) {
          this._botResponses[item.path].entities = [
            ...this._botResponses[item.path].entities,
            ...this._botResponses[item.path].nonMandatoryResponses,
          ];
        }

        this._botResponses[item.path].entities = this._botResponses[
          item.path
        ].entities.map((entity) => {
          if (entity.variants.length === 0) {
            // eslint-disable-next-line no-param-reassign
            entity.variants = [];
          }
          return entity;
        });

        // #region Omnichannel rendering
        const languageCode = item.path;
        let allChannels = [];

        // Updating Bot Says device_platform_map with the default key data.
        let variantsPresent = [];
        const botSaysData = this._botResponses?.[languageCode]?.botSays;
        botSaysData.devicePlatformMap &&
          Object.keys(botSaysData.devicePlatformMap)?.forEach((channel) => {
            variantsPresent = [
              ...variantsPresent,
              ...botSaysData.devicePlatformMap[channel],
            ];
          });

        // Variants that are not present will be stored in variantsNotPresent
        const variantsNotPresent = [
          ...Array(botSaysData?.variants?.length).keys(),
        ].filter((val) => !variantsPresent.includes(val));

        // Add variants which are not present in default key.
        botSaysData.devicePlatformMap = {
          ...botSaysData.devicePlatformMap,
          default: [
            ...(botSaysData.devicePlatformMap?.default
              ? botSaysData.devicePlatformMap.default
              : []),
            ...variantsNotPresent,
          ],
        };

        const botSaysChannels = Object.keys(
          this._botResponses[languageCode]?.botSays?.devicePlatformMap || []
        );

        const delayChannels = Object.keys(
          this._botResponses[languageCode]?.delay?.devicePlatformMap || []
        );

        const finalBotReplyChannels = Object.keys(
          this._botResponses[languageCode]?.finalBotReply?.devicePlatformMap ||
            []
        );

        allChannels = [
          ...botSaysChannels,
          ...delayChannels,
          ...finalBotReplyChannels,
        ];

        if (this._botResponses[languageCode]?.entities.length > 0) {
          this._botResponses[languageCode]?.entities.forEach((entity) => {
            const values = Object.keys(entity?.devicePlatformMap || []);
            allChannels.push(...values);
          });
        }

        const channels = [...new Set(allChannels)];

        const currentBotResponses = cloneDeep(this._botResponses[languageCode]);

        if (channels.length === 0) {
          channels.push('5');
        }

        channels.forEach((id) => {
          const channelID = id === 'default' ? 5 : Number(id);

          // #region BotResponses
          const botResponsesChannelVariants = [];
          const botResponsesDeviceMap =
            currentBotResponses.botSays.devicePlatformMap?.[channelID] ||
            currentBotResponses.botSays.devicePlatformMap?.default;

          currentBotResponses.botSays.variants.forEach((channelItem, index) => {
            if (
              botResponsesDeviceMap !== undefined &&
              botResponsesDeviceMap.includes(index)
            ) {
              botResponsesChannelVariants.push(channelItem);
            }
          });

          const botResponsesDeviceMapWithChannel =
            botResponsesDeviceMap !== undefined
              ? {
                  [channelID]: botResponsesDeviceMap,
                }
              : {};

          if (channelID === 5 && botResponsesChannelVariants.length === 0) {
            this.webSDKSaved = false;
          } else {
            this.webSDKSaved = true;
          }
          // #endregion

          // #region Entities
          const entitiesChannelVariants = [];

          if (currentBotResponses.entities.length > 0) {
            currentBotResponses.entities.forEach((channelItem) => {
              // Variants that are present in device_platform_map
              let variantsPresent = [];
              channelItem.devicePlatformMap &&
                Object.keys(channelItem.devicePlatformMap).forEach(
                  (channel) => {
                    variantsPresent = [
                      ...variantsPresent,
                      ...channelItem.devicePlatformMap[channel],
                    ];
                  }
                );

              // Variants that are not present will be stored in variantsNotPresent
              const variantsNotPresent = [
                ...Array(channelItem?.variants?.length).keys(),
              ].filter((val) => !variantsPresent.includes(val));

              // Add variants which are not present in default key.
              channelItem.devicePlatformMap = {
                ...channelItem.devicePlatformMap,
                default: [
                  ...(channelItem.devicePlatformMap?.default
                    ? channelItem.devicePlatformMap.default
                    : []),
                  ...variantsNotPresent,
                ],
              };

              const entityVariant = cloneDeep(channelItem);
              const entityChannelVariants = [];

              const entitiesDeviceMap =
                channelItem?.devicePlatformMap?.[channelID] ||
                channelItem?.devicePlatformMap?.default;

              const entitiesDeviceMapWithChannel =
                entitiesDeviceMap !== undefined &&
                entitiesDeviceMap.length !== 0
                  ? {
                      [channelID]: entitiesDeviceMap,
                    }
                  : {};

              entityVariant.devicePlatformMap = entitiesDeviceMapWithChannel;

              entityVariant.variants.forEach((channelItem, index) => {
                if (
                  entitiesDeviceMap !== undefined &&
                  entitiesDeviceMap.includes(index)
                ) {
                  entityChannelVariants.push(channelItem);
                }
              });

              entityVariant.variants = entityChannelVariants;
              entitiesChannelVariants.push(entityVariant);
            });
          }

          // #endregion

          // #region FinalBotReply
          const finalBotReplyChannelVariants = [];

          // Add variants which are not present in default key.
          currentBotResponses.finalBotReply[
            'devicePlatformMap'
          ] = updateDevicePlatformMapDefault(
            currentBotResponses.finalBotReply.devicePlatformMap,
            currentBotResponses.finalBotReply.variants
          );

          const finalBotReplyDeviceMap =
            currentBotResponses.finalBotReply.devicePlatformMap?.[channelID] ||
            currentBotResponses.finalBotReply.devicePlatformMap?.default;

          currentBotResponses.finalBotReply.variants.forEach(
            (channelItem, index) => {
              if (
                finalBotReplyDeviceMap !== undefined &&
                finalBotReplyDeviceMap.includes(index)
              ) {
                finalBotReplyChannelVariants.push(channelItem);
              }
            }
          );

          const finalBotReplyDeviceMapWithChannel =
            followUpDeviceMap !== undefined
              ? {
                  [channelID]: followUpDeviceMap,
                }
              : {};
          // #endregion

          // #region FollowUp Messages
          const followUpChannelVariants = [];

          // Add variants which are not present in default key.
          currentBotResponses.delay[
            'devicePlatformMap'
          ] = updateDevicePlatformMapDefault(
            currentBotResponses.delay.devicePlatformMap,
            currentBotResponses.delay.variants
          );

          const followUpDeviceMap =
            currentBotResponses.delay.devicePlatformMap?.[channelID] ||
            currentBotResponses.delay.devicePlatformMap?.default;

          currentBotResponses.delay.variants.forEach((channelItem, index) => {
            if (
              followUpDeviceMap !== undefined &&
              followUpDeviceMap.includes(index)
            ) {
              followUpChannelVariants.push(channelItem);
            }
          });

          const followUpDeviceMapWithChannel =
            followUpDeviceMap !== undefined
              ? {
                  [channelID]: followUpDeviceMap,
                }
              : {};
          // #endregion

          this._botResponsesWithChannel[languageCode] = {
            ...this._botResponsesWithChannel[languageCode],
            [channelID]: {
              ...currentBotResponses,
              channelDataUpdated: true,
              botSays: {
                devicePlatformMap: botResponsesDeviceMapWithChannel,
                quickReplies: currentBotResponses.botSays.quickReplies,
                variants: botResponsesChannelVariants,
              },
              entities: entitiesChannelVariants,
              finalBotReply: {
                devicePlatformMap: finalBotReplyDeviceMapWithChannel,
                quickReplies: currentBotResponses.finalBotReply.quickReplies,
                variants: finalBotReplyChannelVariants,
              },
              delay: {
                devicePlatformMap: followUpDeviceMapWithChannel,
                time: currentBotResponses.delay.time,
                variants: followUpChannelVariants,
              },
            },
          };
        });

        // #endregion
      }
    });

    if (callback) callback();
  },

  /**
   * Getter to fetch the entities on the Node
   * @return {[type]} [description]
   */
  getEntitiesOnNode() {
    const languageCode = this.getCurrentLanguageCode();
    if (this._botResponses[languageCode]) {
      const entities = this._botResponses[languageCode].entities || [];
      const entitiesOnNode = entities.map((entity) => entity.title);
      return entitiesOnNode;
    }

    return [];
  },

  /**
   * Updates delay, entities, and other keys for all languages when once added to any language
   * @param {string} type   if the value will be delay or entities
   * @param {(Object | Array.<Object>)} value  value of the respective type
   * @param {Array.<string>}  list   list's names and their mandatory status' of all entities
   */
  updateMultiLangBotResponses(type, value, allEntities, action) {
    const allSupportedLanguages = [...this._supportedLanguages];
    const currentBotSaysLanguage = this._selectedBotSaysLanguage;
    const hybridSources = CommonStore.getMetaTags()?.hybridLanguageSources;
    const currentChannelId = this._currentChannelID;

    allSupportedLanguages.forEach((item) => {
      const language = item.path;
      const channels = Object.keys(this._botResponsesWithChannel[language]);

      if (
        language !== currentBotSaysLanguage ||
        Object.keys(hybridSources).length > 0
      ) {
        if (type === BotSaysKeys.STATIC_NODE.DELAY) {
          if (hybridSources?.[language]) {
            if (
              this._botResponsesWithChannel[hybridSources[language]]?.[
                currentChannelId
              ]
            ) {
              this._botResponsesWithChannel[hybridSources[language]][
                currentChannelId
              ].delay.time = value;
            }
          }

          channels.forEach((item) => {
            this._botResponsesWithChannel[language][item].delay.time = value;
          });
          this._botResponses[language].delay.time = value;
        }

        // Update the keys for all the channels and languages. One can just call this function with the key and value
        // and can update the if condition comparison.
        if (type === BotSaysKeys.STATIC_NODE.FLUSH_ENTITIES) {
          if (hybridSources?.[language]) {
            if (
              this._botResponsesWithChannel[hybridSources[language]]?.[
                currentChannelId
              ]
            ) {
              this._botResponsesWithChannel[hybridSources[language]][
                currentChannelId
              ][type] = value;
            }
          }

          channels.forEach((item) => {
            this._botResponsesWithChannel[language][item][type] = value;
          });
          this._botResponses[language][type] = value;
        }

        if (type === BotSaysKeys.STATIC_NODE.ENTITIES) {
          let languageEntities = [];

          if (
            this._botResponsesWithChannel[language][currentChannelId] !==
            undefined
          ) {
            languageEntities = [
              ...this._botResponsesWithChannel[language][currentChannelId]
                .entities,
            ];
          } else {
            // If entity is being deleted, just keep languageEntities same as ones in botResponses
            // eslint-disable-next-line no-lonely-if
            if (action === BotSaysKeys.STATIC_NODE.DELETE) {
              languageEntities = [...this._botResponses[language].entities];
            }
          }

          let updatedEntities = [...languageEntities];
          const entitiesArray = [];

          // Adds/deletes entities in other languages
          if (action !== BotSaysKeys.STATIC_NODE.MANDATORY) {
            if (value.length > 0) {
              value.forEach((entityItem) => {
                if (action === BotSaysKeys.STATIC_NODE.ADD) {
                  const alreadyExists = languageEntities.some(
                    (existingEntity) =>
                      existingEntity.title === entityItem.title
                  );
                  if (!alreadyExists) {
                    updatedEntities.push(entityItem);
                  }
                } else if (action === BotSaysKeys.STATIC_NODE.DELETE) {
                  updatedEntities.forEach((entity) => {
                    if (entityItem.title === entity.title) {
                      entitiesArray.push(entity);
                    }
                  });

                  if (entitiesArray.length === value.length) {
                    updatedEntities = entitiesArray;
                  }
                }
              });
            } else {
              updatedEntities = [];
            }

            // We create a fresh array for omnichannel entities when adding/deleting
            channels.forEach((item) => {
              let totalEntities = [];
              const finalEntities = [];

              updatedEntities.forEach((entity) =>
                finalEntities.push(entity.title)
              );

              const channelEntities = cloneDeep(
                this._botResponsesWithChannel[language][item].entities
              );
              if (channelEntities.length !== updatedEntities.length) {
                // OMNICHANNEL: Add an entity
                if (action === BotSaysKeys.STATIC_NODE.ADD) {
                  totalEntities = updatedEntities.filter(
                    ({ title: id1 }) =>
                      !channelEntities.some(({ title: id2 }) => id2 === id1)
                  );

                  this._botResponsesWithChannel[language][item].entities = [
                    ...this._botResponsesWithChannel[language][item].entities,
                    ...totalEntities,
                  ];
                }

                // OMNICHANNEL: Delete an entity
                if (action === BotSaysKeys.STATIC_NODE.DELETE) {
                  totalEntities = channelEntities.filter(({ title }) =>
                    finalEntities.includes(title)
                  );
                  this._botResponsesWithChannel[language][item].entities = [
                    ...totalEntities,
                  ];
                }
              }
            });
          }

          // Updates mandatory status of entities across all languages
          else {
            this._botResponses[language].entities.map((entity) => {
              allEntities.forEach((allEntitiesItem) => {
                if (entity.title === allEntitiesItem.title) {
                  entity.mandatory = allEntitiesItem.mandatory;
                }
                return entity;
              });
            });

            channels.forEach((item) => {
              this._botResponsesWithChannel[language][item].entities.map(
                (entity) => {
                  allEntities.forEach((allEntitiesItem) => {
                    if (entity.title === allEntitiesItem.title) {
                      entity.mandatory = allEntitiesItem.mandatory;
                    }
                    return entity;
                  });
                }
              );
            });
          }
        }

        if (this._botResponsesWithChannel[language][currentChannelId]) {
          this._botResponsesWithChannel[language][currentChannelId].langSrc =
            hybridSources[language] || language;
          this._botResponsesWithChannel[language][
            currentChannelId
          ].langScript = language;
          this._botResponsesWithChannel[language][
            currentChannelId
          ].updated = true;
        }

        // Adds all the supported languages to array of languages which were edited
        const languagesEdited = [...this._editedLanguages];
        if (!languagesEdited.includes(language)) {
          languagesEdited.push(language);
        }
        this._editedLanguages = languagesEdited;
      }
    });
  },

  /**
   * Updates the store with the latest bot responses
   * it also marks the set of responses as updated depending on a flag
   * @param  {[type]}   responses            [description]
   * @param  {Function} callback             [description]
   * @param  {[type]}   meta                 [description]
   * @param  {[type]}   dontMarkAsUpdate     [description]
   * @param  {[type]}   regenerateTruthTable [description]
   * @return {[type]}                        [description]
   */
  updateBotResponses(
    responses,
    callback,
    meta,
    dontMarkAsUpdate,
    regenerateTruthTable,
    editedLanguage,
    currentHybridLanguage
  ) {
    // Updates langauge specific responses and data
    const currentBotSaysLanguage = this._selectedBotSaysLanguage;
    const { type, subType } = responses;
    const hybridSources = CommonStore.getMetaTags()?.hybridLanguageSources;
    const currentChannelId = this._currentChannelID;

    let language = currentBotSaysLanguage;

    delete responses.type;
    delete responses.subType;

    if (currentHybridLanguage) {
      language = currentHybridLanguage;
    }

    const allUpdatedResponses = cloneDeep(responses);

    // Based on this flag, we determine if we're to save channel responses as DPSV
    allUpdatedResponses.channelDataUpdated = true;

    this._botResponsesWithChannel[language][
      currentChannelId
    ] = allUpdatedResponses;

    if (
      this._botResponsesWithChannel[language][currentChannelId].delay
        .variants[0] === undefined
    ) {
      this._botResponsesWithChannel[language][
        currentChannelId
      ].delay.variants.shift();
    }

    this._botResponsesWithChannel[language][currentChannelId].langSrc =
      hybridSources[language] || language;
    this._botResponsesWithChannel[language][
      currentChannelId
    ].langScript = language.split('|')[0];

    const dpsvChannels = this._botResponses[language].dpsvChannels;
    this._botResponses[language] = responses;
    this._botResponses[language].dpsvChannels = dpsvChannels;

    this._botResponses[language].langSrc = hybridSources[language] || language;
    this._botResponses[language].langScript = language.split('|')[0];

    // Adds language to array of languages which were edited
    const languagesEdited = [...this._editedLanguages];
    if (!languagesEdited.includes(editedLanguage)) {
      languagesEdited.push(editedLanguage);
    }
    this._editedLanguages = languagesEdited;

    if (this._isSuperman) {
      const {
        DELAY,
        TIME,
        ENTITIES,
        ADD,
        DELETE,
        MANDATORY,
        FLUSH_ENTITIES,
      } = BotSaysKeys.STATIC_NODE;

      // Updates delay, entities and entity flusher on all languages when added/removed/changed mandatory status in one
      if (type === DELAY && subType === TIME) {
        this.updateMultiLangBotResponses(DELAY, responses.delay.time);
      }

      if (type === FLUSH_ENTITIES) {
        this.updateMultiLangBotResponses(
          FLUSH_ENTITIES,
          responses.flushEntities
        );
      }

      if (
        type === ENTITIES &&
        (subType === ADD || subType === DELETE || subType === MANDATORY)
      ) {
        // Generates an array of objects containing entity name and its respective mandatory flags
        const allEntities = [];
        responses.entities.forEach((entity) => {
          const entityData = {
            title: entity.title,
            mandatory: entity.mandatory,
          };
          allEntities.push(entityData);
        });

        this.updateMultiLangBotResponses(
          ENTITIES,
          responses.entities,
          allEntities,
          subType
        );
      }
    }

    if (!dontMarkAsUpdate) {
      this._botResponses[language].updated = true;
    }
    if (regenerateTruthTable) {
      this._botResponses[language].regenerateTruthTable = true;
    }
    if (callback) callback();
  },

  /**
   * Reset the updated flag for bot responses
   */
  resetBotResponsesUpdateStatus() {
    const allSupportedLanguages = this._supportedLanguages;
    allSupportedLanguages.forEach((item) => {
      this._botResponses[item.path].updated = false;
      this._originalResponses[item.path] = JSON.parse(
        JSON.stringify(this._botResponses[item.path])
      );
    });
  },

  // Gets current hybrid language
  getCurrentHybridLanguage() {
    return this._currentHybridLanguage;
  },

  setCurrentChannelId(channelId) {
    this._currentChannelID = channelId;
    this.removeAutoTranslatedChannelData();
  },

  getCurrentChannelId() {
    return this._currentChannelID;
  },

  getCurrentLanguageCode() {
    const hybridSources = CommonStore.getMetaTags()?.hybridLanguageSources;

    const currentHybridLang = Object.keys(hybridSources).find(
      (key) => hybridSources[key] === this._selectedBotSaysLanguage
    );

    return currentHybridLang === this._currentHybridLanguage
      ? currentHybridLang
      : this._selectedBotSaysLanguage;
  },

  updateBotResponsesByChannel(data, callback) {
    const channelData = humps.camelizeKeys({ ...data });
    const selectedLangCode = this.getCurrentLanguageCode();

    const currentBotResponseData = cloneDeep({
      ...this._botResponses[selectedLangCode],
    });

    // Creates default entity data if auto-translations are empty
    const currentBotEntitiesData = Object.keys(
      cloneDeep({
        ...this._botResponses[selectedLangCode].entities,
      })
    );
    const defaultEntitiesData = [];

    if (currentBotEntitiesData.length > 0) {
      currentBotEntitiesData.forEach((item) => {
        const entity = {
          ...this._botResponses[selectedLangCode].entities[item],
        };
        entity.variants = [];
        entity.devicePlatformMap = {};
        defaultEntitiesData.push(entity);
      });
    }

    // Used for checking if channel is to be saved as DPSV
    currentBotResponseData.channelDataUpdated = false;

    currentBotResponseData.botSays.variants =
      channelData?.botSays?.variants || [];
    currentBotResponseData.botSays.devicePlatformMap = {
      [this._currentChannelID]: [0],
    };
    currentBotResponseData.entities =
      channelData?.entities || defaultEntitiesData || [];

    currentBotResponseData.finalBotReply.variants =
      channelData?.finalBotResponse?.variants || [];

    currentBotResponseData.finalBotReply.devicePlatformMap = {
      [this._currentChannelID]: [0],
    };

    currentBotResponseData.delay.variants = channelData?.delay?.variants || [];

    currentBotResponseData.delay.devicePlatformMap = {
      [this._currentChannelID]: [0],
    };

    this._botResponsesWithChannel[selectedLangCode] = {
      ...this._botResponsesWithChannel[selectedLangCode],
      [this._currentChannelID]: currentBotResponseData,
    };

    if (!this._editedLanguages.includes(this._selectedBotSaysLanguage)) {
      this._editedLanguages.push(this._selectedBotSaysLanguage);
    }

    if (callback) callback();
  },

  // Removes auto-translated data from store, if its not edited
  removeAutoTranslatedChannelData() {
    const selectedLangCode = this.getCurrentLanguageCode();
    const currentChannelData = cloneDeep(
      this._botResponsesWithChannel[selectedLangCode]
    );

    const currentBotResponseChannels = Object.keys(
      currentChannelData
    ).map((i) => Number(i));

    currentBotResponseChannels.forEach((item) => {
      if (!currentChannelData[item].channelDataUpdated) {
        delete this._botResponsesWithChannel[selectedLangCode][item];
      }
    });
  },

  getBotResponsesWithChannel() {
    return this._botResponsesWithChannel;
  },

  setDPSVChannels(botResponses = this._botResponses) {
    let language = this._selectedBotSaysLanguage;
    if (this._currentHybridLanguage) {
      language = this._currentHybridLanguage;
    }
    const responses = botResponses[language] || {};
    let allChannels = [];

    const botSaysChannels = Object.keys(
      responses?.botSays?.devicePlatformMap || []
    );

    const delayChannels = Object.keys(
      responses?.delay?.devicePlatformMap || []
    );

    const finalBotReplyChannels = Object.keys(
      responses?.finalBotReply?.devicePlatformMap || []
    );

    const initialDPSVChannels = responses?.dpsvChannels || [];

    allChannels = [
      ...botSaysChannels,
      ...delayChannels,
      ...finalBotReplyChannels,
      ...initialDPSVChannels,
    ];

    if (responses?.entities.length > 0) {
      responses?.entities.forEach((entity) => {
        const values = Object.keys(entity?.devicePlatformMap || []);
        allChannels.push(...values);
      });
    }
    allChannels = [...new Set(allChannels)];
    this._botResponses[language].dpsvChannels = allChannels;
  },

  updateDPSVChannels(botResponses = this._botResponses) {
    let language = this._selectedBotSaysLanguage;
    if (this._currentHybridLanguage) {
      language = this._currentHybridLanguage;
    }
    const responses = botResponses[language] || {};
    let allVariants = [];

    const botSaysVariants = responses?.botSays?.variants || [];
    const delayVariants = responses?.delay?.variants || [];
    const finalBotReplyVariants = responses?.finalBotReply?.variants || [];

    allVariants = [
      ...botSaysVariants,
      ...delayVariants,
      ...finalBotReplyVariants,
    ];

    if (responses?.entities.length > 0) {
      responses?.entities.forEach((entity) => {
        const values = entity?.variants || [];
        allVariants.push(...values);
      });
    }

    const filteredDPSVChannels =
      responses?.dpsvChannels?.filter(
        (channelId) => parseInt(channelId) !== this._currentChannelID
      ) || [];

    if (allVariants.length) {
      this._botResponses[language].dpsvChannels = [
        ...filteredDPSVChannels,
        this._currentChannelID.toString(),
      ];
      return;
    }
    this._botResponses[language].dpsvChannels = filteredDPSVChannels;
  },
});

/**
 * Listen to actions that are dispatched by the dispatcher and accordingly
 * call some store functions depending on the ActionType
 * @type {[type]}
 */
BotSaysStore.dispatchToken = register((action) => {
  const actionType = action.type;
  const isSuperman = BotSaysStore.getIsSuperman();
  action = action.action;

  switch (actionType) {
    case ActionTypes.SET_BOT_RESPONSES:
      BotSaysStore.setSupportedLanguages(
        action.meta.domain.language_list,
        action.meta.all_languages
      );

      BotSaysStore.setCurrentBotSaysLanguage(
        !isSuperman ? action.languageCode : DEFAULT_LANGUAGE
      );

      BotSaysStore.setBotResponses(action.data);
      BotSaysStore.setDPSVChannels(action.data);
      BotSaysStore.emitChange();
      if (action.callback) {
        setTimeout(action.callback, 0);
      }
      break;

    case ActionTypes.UPDATE_BOT_RESPONSES:
      BotSaysStore.updateBotResponses(
        action.data,
        action.callback,
        null,
        action.dontMarkAsUpdate,
        action.regenerateTruthTable,
        action.editedLanguage,
        action.currentHybridLanguage
      );
      BotSaysStore.emitChange();
      break;

    case ActionTypes.SAVE_BOT_RESPONSES:
      BotSaysStore.resetEditedLanguages();
      BotSaysStore.setWebSDKSaved();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.SET_URL_METADATA:
      /**
       * Resetting the bot says store is commented out because sometimes URL Meta calls finishes
       * after botResponses API call which leads to resetting of the data.
       */
      // BotSaysStore.reset();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.RESET_EDITED:
      BotSaysStore.resetEdited();
      BotSaysStore.setDPSVChannels();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.UPDATE_CURRENT_LANGUAGE:
      BotSaysStore.reset();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.SET_CURRENT_BOTSAYS_LANGUAGE:
      BotSaysStore.setCurrentBotSaysLanguage(
        action.languageCode,
        action.hybridLanguage
      );
      BotSaysStore.setDPSVChannels();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.RESET_BOT_RESPONSE_UPDATE_DATA:
      BotSaysStore.resetBotResponsesUpdateStatus();
      BotSaysStore.emitChange();
      break;

    case ActionTypes.SET_IS_SUPERMAN:
      BotSaysStore.setIsSuperman(action.isSuperman);
      BotSaysStore.emitChange();
      break;

    case ActionTypes.SET_CURRENT_CHANNEL_ID:
      BotSaysStore.setCurrentChannelId(action.channelId);
      BotSaysStore.emitChange();
      break;

    case ActionTypes.UPDATE_BOT_RESPONSES_BY_CHANNEL:
      BotSaysStore.updateBotResponsesByChannel(action.data, action.callback);
      BotSaysStore.emitChange();
      break;

    case ActionTypes.BOT_RESPONSES_RECEIVED:
      BotSaysStore.updateDPSVChannels();
      BotSaysStore.emitChange();
      break;

    default:
  }
});

export default BotSaysStore;
